// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The evidence tile's Foto view (v8 `evPhoto`): every sensor frame that sees
 * the part (`GET /instances/{cloud}/{id}/frames`, most central first) as a
 * grid, with the part's projected centroid (`u`,`v`) ringed on each. A frame
 * opens large, with "Alle frames" back and "Del op fra frame N" on to the
 * SAM3 view, seeded at that pixel.
 *
 * `u`,`v` are pixels of the sensor's image grid, so the ring is placed with
 * the project's frame size (`GET /frames` width/height); without it the ring
 * is left out rather than guessed.
 *
 * Above the frames sits the part's resource image (`sys:view`, frontend-r22.1):
 * the one chosen image, cropped to its region (`ViewImage`), or "Intet
 * billede valgt" — never the most central frame standing in for it. An
 * editor chooses a frame with "Brug som ressourcebillede"; the chosen one is
 * marked "Valgt" (`photoThumbState`).
 */

import { useState, type CSSProperties } from 'react';

import { api } from '../../../api/client';
import type { ResourceView, VisibleFrame } from '../../../api/types';
import { useAsync } from '../../../app/useAsync';
import { fotoViewHint, frameView, photoThumbState, VIEW_TEXT, viewCaption } from '../../../kortlaegning/view';
import { formatNumber } from '../../../kortlaegning/vocab';
import { Button } from '../../Button';
import { TextLink } from '../../TextLink';
import { ViewImage } from '../../kortlaegning/ViewImage';
import styles from './FotoGrid.module.css';

/** Thumbnail edge asked of the server. */
const THUMB_SIZE = 320;
/** The large view's edge. */
const LARGE_SIZE = 1280;
/** Thumbnails drawn before "Vis alle". */
const GRID_PAGE = 24;

export interface FotoGridProps {
  /** The part's frames; null while loading or when there are none (see `message`). */
  frames: readonly VisibleFrame[] | null;
  /** What stands in for the grid (loading, none, failed, unlinked). */
  message: string | null;
  /** The frame shown large, or null for the grid. */
  open: number | null;
  onOpen: (frameId: number | null) => void;
  /** "Del op fra frame N". */
  onSegment: (frame: VisibleFrame) => void;
  /** The part's resource image (`sys:view`), or null. */
  view: ResourceView | null;
  /** Whether a choice has a part to go to (`selectionView(...).target !== null`). */
  hasTarget: boolean;
  /** "Brug som ressourcebillede"; absent = read-only. */
  onChooseView?: (view: ResourceView) => void;
}

/** The chosen image's edge in the bar. */
const VIEW_THUMB = 96;

/** The resource image bar: the chosen image and its caption, or "Intet billede valgt" (+ the hint for an editor). */
function ViewBar({
  view,
  hasTarget,
  canChoose,
  onShow,
}: {
  view: ResourceView | null;
  hasTarget: boolean;
  canChoose: boolean;
  /** Open the chosen frame large (when it is one of the part's frames). */
  onShow?: () => void;
}) {
  const hint = fotoViewHint(view, canChoose, hasTarget);
  return (
    <div className={styles.viewBar}>
      {view ? (
        onShow ? (
          <button type="button" className={styles.viewThumb} onClick={onShow} aria-label={`${viewCaption(view)} — vis stort`}>
            <ViewImage view={view} displaySize={VIEW_THUMB} maxFetch={512} label={null} className={styles.viewImage} />
          </button>
        ) : (
          <span className={styles.viewThumb}>
            <ViewImage view={view} displaySize={VIEW_THUMB} maxFetch={512} label={viewCaption(view)} className={styles.viewImage} />
          </span>
        )
      ) : (
        <span className={`${styles.viewThumb} ${styles.viewNone}`} aria-hidden="true" />
      )}
      <span className={styles.viewText}>
        <b>{view ? viewCaption(view) : VIEW_TEXT.none}</b>
        {hint && <span className={styles.muted}>Vælg en frame herunder eller en 360°-optagelse med »{VIEW_TEXT.use}«.</span>}
        {view && canChoose && <span className={styles.muted}>Vælg en anden frame eller 360° for at skifte billedet.</span>}
      </span>
    </div>
  );
}

/** A grid cell's choose control: »Valgt« on the chosen frame, else »Brug som ressourcebillede« for an editor. */
function ChooseChip({ frameId, view, onChoose }: { frameId: number; view: ResourceView | null; onChoose?: (view: ResourceView) => void }) {
  const { chosen, label } = photoThumbState(view, frameId);
  if (chosen) return <span className={`${styles.chip} ${styles.chosen}`}>{VIEW_TEXT.chosen}</span>;
  if (!onChoose) return null;
  return (
    <button type="button" className={styles.chip} aria-label={label} title={VIEW_TEXT.use} onClick={() => onChoose(frameView(frameId))}>
      Brug
    </button>
  );
}

interface Dims {
  width: number;
  height: number;
}

function markStyle(f: VisibleFrame, dims: Dims | null): CSSProperties | null {
  if (!dims || !(dims.width > 0) || !(dims.height > 0)) return null;
  const x = f.u / dims.width;
  const y = f.v / dims.height;
  if (x < 0 || x > 1 || y < 0 || y > 1) return null;
  return { left: `${x * 100}%`, top: `${y * 100}%` };
}

function Thumb({ frame, dims, large }: { frame: VisibleFrame; dims: Dims | null; large?: boolean }) {
  const [failed, setFailed] = useState(false);
  const mark = markStyle(frame, dims);
  // The frame box takes the sensor's aspect, so the ring lands where u,v are.
  const aspect = (dims ? { '--ar': String(dims.width / dims.height) } : undefined) as CSSProperties | undefined;
  return (
    <span className={large ? `${styles.frame} ${styles.large}` : styles.frame} style={aspect}>
      {failed ? (
        <span className={styles.failed}>Billedet kunne ikke hentes</span>
      ) : (
        <img
          className={styles.image}
          src={api.frameImageUrl(frame.frame_id, 'color', { maxSize: large ? LARGE_SIZE : THUMB_SIZE })}
          alt={large ? `Frame ${frame.frame_id}, delen markeret` : ''}
          loading={large ? undefined : 'lazy'}
          draggable={false}
          onError={() => setFailed(true)}
        />
      )}
      {mark && !failed && <span className={styles.mark} style={mark} aria-hidden="true" />}
    </span>
  );
}

export function FotoGrid({ frames, message, open, onOpen, onSegment, view, hasTarget, onChooseView }: FotoGridProps) {
  const list = useAsync((signal) => api.frames({}, signal), []);
  const fl = list.data;
  const dims: Dims | null = fl && fl.width && fl.height ? { width: fl.width, height: fl.height } : null;
  const [all, setAll] = useState(false);

  const canChoose = !!onChooseView;
  const chosenFrame = view?.kind === 'frame' && frames?.some((f) => f.frame_id === view.id) ? view.id : null;
  const bar = (
    <ViewBar
      view={view}
      hasTarget={hasTarget}
      canChoose={canChoose}
      onShow={chosenFrame !== null && chosenFrame !== open ? () => onOpen(chosenFrame) : undefined}
    />
  );

  if (!frames || frames.length === 0) {
    return (
      <div className={styles.view}>
        {bar}
        <div className={styles.media}>
          <p className={styles.empty}>{message}</p>
        </div>
      </div>
    );
  }

  const at = open === null ? -1 : frames.findIndex((f) => f.frame_id === open);
  if (at >= 0) {
    const f = frames[at];
    const prev = frames[at - 1];
    const next = frames[at + 1];
    const thumb = photoThumbState(view, f.frame_id);
    return (
      <div className={styles.view}>
        {bar}
        <div className={`${styles.media} ${styles.stage}`}>
          <Thumb frame={f} dims={dims} large />
          <span className={styles.label}>
            Frame <span className="mono">{f.frame_id}</span> · {at + 1} af {frames.length} · {formatNumber(f.depth)} m fra kameraet
          </span>
        </div>
        <div className={styles.foot}>
          <TextLink size="sm" onClick={() => onOpen(null)}>
            Alle frames
          </TextLink>
          <Button onClick={() => prev && onOpen(prev.frame_id)} disabled={!prev} aria-label="Forrige frame">
            ‹
          </Button>
          <Button onClick={() => next && onOpen(next.frame_id)} disabled={!next} aria-label="Næste frame">
            ›
          </Button>
          {thumb.chosen ? (
            <span className={`${styles.chip} ${styles.chosen} ${styles.end}`}>{VIEW_TEXT.chosen} ✓</span>
          ) : (
            onChooseView && (
              <Button className={styles.end} onClick={() => onChooseView(frameView(f.frame_id))}>
                {VIEW_TEXT.use}
              </Button>
            )
          )}
          <Button
            variant="primary"
            className={thumb.chosen || onChooseView ? undefined : styles.end}
            onClick={() => onSegment(f)}
          >
            Del op fra frame {f.frame_id}
          </Button>
        </div>
      </div>
    );
  }

  const shown = all ? frames : frames.slice(0, GRID_PAGE);
  return (
    <div className={styles.view}>
      {bar}
      <ul className={styles.grid} aria-label="Frames der ser delen">
        {shown.map((f, i) => (
          <li key={f.frame_id} className={styles.item}>
            <button
              type="button"
              className={styles.cell}
              onClick={() => onOpen(f.frame_id)}
              aria-label={`Frame ${f.frame_id}${i === 0 ? ' (mest central)' : ''} — vis stort`}
            >
              <Thumb frame={f} dims={dims} />
              <span className={styles.label}>
                <span className="mono">{f.frame_id}</span>
              </span>
            </button>
            <ChooseChip frameId={f.frame_id} view={view} onChoose={onChooseView} />
          </li>
        ))}
      </ul>
      <div className={styles.foot}>
        <span className={styles.muted}>
          {frames.length === 1 ? '1 frame' : `${frames.length} frames`} · sorteret efter hvor central delen er
        </span>
        {frames.length > GRID_PAGE && (
          <TextLink size="sm" className={styles.end} onClick={() => setAll((v) => !v)}>
            {all ? `Vis de første ${GRID_PAGE}` : `Vis alle ${frames.length}`}
          </TextLink>
        )}
      </div>
    </div>
  );
}
